// DeleteBusinessModal — US-118.
//
// Cascade decision (documented in the W5b.2 report):
//   - **Active campaigns BLOCK the delete** (status ∈ {active, paused,
//     paused_auto}). Surfaces an "blocked" banner with the campaign count
//     and a `Cancel` action only.
//   - Otherwise we **soft-archive the business** (`archivedAt` = now) and
//     **orphan the leads** (`business_id = NULL`). Draft campaigns remain
//     attached and must be rebound before they can run.

import { useT } from '@barel/i18n';
import {
  Banner,
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Spinner,
} from '@barel/ui';

export interface DeleteBusinessImpact {
  /** Number of leads currently bound to this business. */
  leadsCount: number;
  /** Number of active / paused campaigns blocking the delete. */
  activeCampaignsCount: number;
  /** Number of draft campaigns that remain attached and need rebinding. */
  draftCampaignsCount: number;
  /** Number of pending AI replies that will be cancelled. */
  pendingAiDraftsCount: number;
}

export interface DeleteBusinessModalProps {
  /** Business display name — rendered in the title. */
  name: string;
  /** Cascade impact, computed by the editor before opening the modal. */
  impact?: DeleteBusinessImpact;
  /** Impact is loading/refetching; confirmation stays unavailable. */
  loading?: boolean;
  /** Impact failed to load; confirmation stays unavailable. */
  error?: boolean;
  /** Re-fetches the impact after a load failure. */
  onRetry?: () => void;
  /** Open state — controlled by the parent (US-117 form shell). */
  open: boolean;
  /** Closes the modal without deleting. */
  onCancel: () => void;
  /** Confirms the delete (only callable when nothing blocks). */
  onConfirm: () => void;
  /** Pending state — disables the confirm button while the mutation runs. */
  pending?: boolean;
}

export function DeleteBusinessModal({
  name,
  impact,
  loading = false,
  error = false,
  onRetry,
  open,
  onCancel,
  onConfirm,
  pending = false,
}: DeleteBusinessModalProps) {
  const t = useT('businesses');
  const blocked = Boolean(impact && impact.activeCampaignsCount > 0);
  const impactReady = Boolean(impact && !loading && !error);

  return (
    <Dialog open={open} onOpenChange={(next) => (!next ? onCancel() : undefined)}>
      <DialogContent
        size="md"
        data-testid="business-delete-modal"
        onEscapeKeyDown={(event) => {
          if (pending) event.preventDefault();
        }}
      >
        <DialogHeader
          title={t('delete.title', { name })}
          description={t('delete.subtitle')}
          onClose={onCancel}
        />

        <DialogBody>
          {error ? (
            <Banner
              tone="error"
              data-testid="business-delete-impact-error"
              action={
                onRetry ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={onRetry}
                    data-testid="business-delete-impact-retry"
                  >
                    {t('delete.retry')}
                  </Button>
                ) : undefined
              }
            >
              {t('delete.impactError')}
            </Banner>
          ) : loading || !impact ? (
            <div
              className="flex items-center gap-2 text-sm text-secondary"
              data-testid="business-delete-impact-loading"
            >
              <Spinner size="sm" />
              <span>{t('delete.impactLoading')}</span>
            </div>
          ) : blocked ? (
            <Banner tone="error" data-testid="business-delete-blocked">
              <div className="flex flex-col gap-2">
                <strong>{t('delete.blockedTitle')}</strong>
                <span>{t('delete.blockedBody')}</span>
                <span>
                  {t('delete.impactCampaignsActive', {
                    count: impact.activeCampaignsCount,
                  })}
                </span>
              </div>
            </Banner>
          ) : (
            <div className="flex flex-col gap-3" data-testid="business-delete-impact">
              <h3 className="text-sm font-semibold text-primary">{t('delete.impactHeader')}</h3>
              <ul className="flex flex-col gap-2 text-sm text-secondary">
                <li data-testid="business-delete-impact-leads">
                  {t('delete.impactLeads', { count: impact.leadsCount })}
                </li>
                <li data-testid="business-delete-impact-drafts">
                  {t('delete.impactCampaignsDraft', { count: impact.draftCampaignsCount })}
                </li>
                <li data-testid="business-delete-impact-ai-drafts">
                  {t('delete.impactAiDrafts', { count: impact.pendingAiDraftsCount })}
                </li>
              </ul>
              <p className="text-xs text-tertiary">{t('delete.cascadeNote')}</p>
            </div>
          )}
        </DialogBody>

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            size="md"
            onClick={onCancel}
            data-testid="business-delete-cancel"
            disabled={pending}
          >
            {t('delete.cancel')}
          </Button>
          {!impactReady || blocked ? null : (
            <Button
              type="button"
              variant="danger"
              size="md"
              loading={pending}
              onClick={onConfirm}
              data-testid="business-delete-confirm"
            >
              {t('delete.confirm')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
